import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Search, Mail, Phone, MapPin, Calendar, Users, X } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminSelect, AdminTextarea, StatusPill, EmptyState } from "@/admin/components/ui";

const STATUSES = ["all", "new", "contacted", "quoted", "booked", "completed", "archived"];
const SET_STATUSES = ["new", "contacted", "quoted", "booked", "completed", "archived"];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function AdminHires() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [stat, setStat] = useState("new");

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/hires").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const types = useMemo(() => ["all", ...Array.from(new Set(items.map((i) => i.event_type).filter(Boolean)))], [items]);

  const list = useMemo(() => items.filter((h) =>
    (status === "all" || (h.status || "new") === status) &&
    (typeFilter === "all" || h.event_type === typeFilter) &&
    (!q || `${h.name} ${h.surname} ${h.email} ${h.event_type}`.toLowerCase().includes(q.toLowerCase()))
  ), [items, q, status, typeFilter]);

  const openDetail = (h) => {
    setOpen(h);
    setAdminNotes(h.admin_notes || "");
    setStat(h.status || "new");
  };

  const update = async () => {
    try {
      await adminApi.put(`/admin/hires/${open.id}`, { status: stat, admin_notes: adminNotes });
      toast.success("Request updated.");
      setOpen(null); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="page-admin-hires" className="space-y-6">
      <SEO title="AdminHires · WelkomBiz Photography" noindex />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="hires-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hire requests…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`hires-filter-${s}`} onClick={() => setStatus(s)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${status === s ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>{s}</button>
        ))}
        <div className="ml-auto">
          <select data-testid="hires-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-black/40 border border-white/10 rounded-full px-4 py-1.5 text-[10px] tracking-luxe uppercase text-white outline-none">
            {types.map((t) => <option key={t} value={t} className="bg-black">{t}</option>)}
          </select>
        </div>
      </div>

      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       list.length === 0 ? <EmptyState testId="hires-empty" title="No requests match" message="Try a different filter or search." /> :
       (
        <Panel className="!p-0 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">Client</th>
                  <th className="text-left px-5 py-3">Event Type</th>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Service</th>
                  <th className="text-left px-5 py-3">Submitted</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.map((h) => (
                  <tr key={h.id} data-testid={`hire-row-${h.id}`} onClick={() => openDetail(h)} className="border-t border-white/5 hover:bg-white/5 cursor-pointer">
                    <td className="px-5 py-3 text-white">{h.name} {h.surname}<br /><span className="text-xs text-white/55">{h.email}</span></td>
                    <td className="px-5 py-3 text-white/70">{h.event_type}</td>
                    <td className="px-5 py-3 text-white/70">{h.event_date || "—"}</td>
                    <td className="px-5 py-3 text-white/70">{h.service_needed}</td>
                    <td className="px-5 py-3 text-white/45 text-xs">{fmtDate(h.created_at)}</td>
                    <td className="px-5 py-3"><StatusPill status={h.status || "new"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-white/5">
            {list.map((h) => (
              <button key={h.id} data-testid={`hire-card-${h.id}`} onClick={() => openDetail(h)} className="w-full text-left p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white truncate">{h.name} {h.surname}</p>
                    <p className="text-xs text-white/55 truncate">{h.event_type} · {h.service_needed}</p>
                  </div>
                  <StatusPill status={h.status || "new"} />
                </div>
                <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-2">{fmtDate(h.created_at)}</p>
              </button>
            ))}
          </div>
        </Panel>
       )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="hire-detail-modal" className="glass-strong chrome-border rounded-md p-6 sm:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div className="min-w-0">
                <p className="text-[10px] tracking-luxe uppercase text-white/45">Hire Request · {open.event_type}</p>
                <h3 className="font-display text-2xl text-chrome">{open.name} {open.surname}</h3>
                <p className="text-xs text-white/50 mt-1">{fmtDate(open.created_at)}</p>
              </div>
              <button onClick={() => setOpen(null)} className="w-9 h-9 rounded-full chrome-border bg-black/40 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <Info icon={Mail} label="Email"><a className="text-white" href={`mailto:${open.email}`}>{open.email}</a></Info>
              <Info icon={Phone} label="Phone">{open.phone || "—"}</Info>
              <Info icon={Calendar} label="Event Date">{open.event_date || "—"}</Info>
              <Info icon={MapPin} label="Location">{open.event_location || "—"}</Info>
              <Info icon={Users} label="People">{open.estimated_people || "—"}</Info>
              <Info label="Service">{open.service_needed}</Info>
            </div>
            {open.message && (
              <div className="glass rounded-md p-4 mb-5">
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">Client Message</p>
                <p className="text-sm text-white/85 font-body whitespace-pre-wrap">{open.message}</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AdminSelect data-testid="hire-status-select" label="Status" value={stat} onChange={(e) => setStat(e.target.value)}>
                {SET_STATUSES.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
              </AdminSelect>
              <div />
              <AdminTextarea data-testid="hire-notes" label="Admin Notes" rows={3} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="sm:col-span-2" />
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="hire-update" onClick={update} className="btn-primary !py-2.5 !px-5 !text-[11px]">Update Request</button>
              <button onClick={() => setOpen(null)} className="btn-ghost !py-2.5 !px-5 !text-[11px]">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ icon: Icon, label, children }) => (
  <div className="glass rounded-md p-3">
    <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-1 inline-flex items-center gap-1.5">
      {Icon && <Icon className="w-3 h-3" />}{label}
    </p>
    <p className="text-sm text-white/85">{children}</p>
  </div>
);
